import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { SmartLink } from '../components/SmartLink';
import { CategoryChip } from '../components/articles/ArticleMeta';
import { ArticleBlockView } from '../components/articles/ArticleBlockView';
import { ArticleToc, MobileArticleToc } from '../components/articles/ArticleToc';
import { ShareButtons } from '../components/articles/ShareButtons';
import { SidebarProductCard } from '../components/articles/SidebarProductCard';
import { ArticleFeedback, ArticleTags, AuthorBio } from '../components/articles/ArticleFooter';
import { ArticleCard } from '../components/articles/ArticleCard';
import { PlaceholderNote } from '../components/company/PlaceholderNote';
import { CtaBand } from '../components/company/CtaBand';
import { useScrollSpy } from '../hooks/useScrollSpy';
import { articles, getArticle } from '../data/articles/posts';
import { getAuthor } from '../data/articles/authors';
import { productCatalog } from '../data/products/shared';
import type { Article, ArticleSection } from '../types/article';
import { NotFound } from './NotFound';
export function BlogPost() {
  const {
    slug = ''
  } = useParams();
  const article = getArticle(slug);
  if (!article) return <NotFound data-id="element-1768" data-mp="x376gz0hx8h1" />;
  return <ArticleView key={article.slug} article={article} data-id="element-1769" data-mp="x2p21f3ojny1" />;
}
function ArticleView({
  article
}: {
  article: Article;
}) {
  const author = getAuthor(article.authorId);
  const isDraft = !article.sections;
  const sections = article.sections ?? draftSections(article);
  const tocItems = sections.map(s => ({
    id: s.id,
    title: s.title
  }));
  const activeId = useScrollSpy(tocItems.map(t => t.id));
  const articleRef = useRef<HTMLDivElement>(null);
  const [barVisible, setBarVisible] = useState(false);
  useEffect(() => {
    const el = articleRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setBarVisible(entry.isIntersecting), {
      rootMargin: '0px 0px -30% 0px'
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const related = [...articles.filter(a => a.slug !== article.slug && a.category === article.category), ...articles.filter(a => a.slug !== article.slug && a.category !== article.category)].slice(0, 3);
  return <main data-id="element-1770" data-mp="23gk381taw6vt">
      <header className="w-full bg-white" data-id="element-1771" data-mp="23gtudsq30k33">
        <div className="mx-auto max-w-shell px-6 pt-6" data-id="element-1772" data-mp="1zfrwbf7wie2p">
          <nav aria-label="Breadcrumb" data-id="element-1773" data-mp="1zhb9gwy9g909">
            <ol className="flex flex-wrap items-center gap-1.5 text-[13px] text-ink-muted" data-id="element-1774" data-mp="1up1lhmgphj0m">
              <li data-id="element-1775" data-mp="1ppyis8o77ovt">
                <SmartLink href="/blog" className="transition-colors duration-150 ease-out hover:text-ink" data-id="element-1776" data-mp="1ppfa7ikfucug">
                  Blog
                </SmartLink>
              </li>
              <li aria-hidden="true" data-id="element-1777" data-mp="1frrfgsmu15ca">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1778" data-mp="1fqpr5mgt2mcm" />
              </li>
              <li aria-current="page" className="font-medium text-ink" data-id="element-1779" data-mp="1bpnpz03bw080">
                {article.category}
              </li>
            </ol>
          </nav>

          <div className="mt-10 max-w-[820px] lg:mt-14" data-id="element-1780" data-mp="3nd9abo1821f4">
            <CategoryChip label={article.category} data-id="element-1781" data-mp="3recq6aj9rxgc" />
            <h1 className="display mt-5 text-[34px] font-semibold text-ink sm:text-[46px] lg:text-[54px]" data-id="element-1782" data-mp="3rgyzaelt6l0o">{article.title}</h1>
            <p className="mt-5 text-[19px] leading-[1.5] text-ink-soft" data-id="element-1783" data-mp="57o34q1wwgyv">{article.subtitle}</p>
            <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-1784" data-mp="33ptmk8b608ul">
              <div className="flex items-center gap-3" data-id="element-1785" data-mp="38od5dzugp4iq">
                <img src={author.photo} alt="" className="h-12 w-12 rounded-full object-cover" data-id="element-1786" data-mp="38owecr24wikm" />
                <div data-id="element-1787" data-mp="3dmescvv0dnzh">
                  <p className="text-[15px] font-semibold text-ink" data-id="element-1788" data-mp="3dlwope60o7hj">{author.name}</p>
                  <p className="text-[13px] text-ink-muted" data-id="element-1789" data-mp="3igj0hig0g225">
                    {author.role} · <time data-id="element-1790" data-mp="2kyiwftd9k03r">{article.date}</time> · {article.readMinutes} min read
                  </p>
                </div>
              </div>
              <div className="hidden lg:block" data-id="element-1791" data-mp="vhwtax7rej64">
                <ShareButtons title={article.title} data-id="element-1792" data-mp="viq4qbhnxwul" />
              </div>
            </div>
          </div>
          <div className="mt-8 lg:hidden" data-id="element-1793" data-mp="rhwf9doko9zl">
            <MobileArticleToc items={tocItems} activeId={activeId} data-id="element-1794" data-mp="157q43w02uu9b" />
          </div>
          <img src={article.image} alt={article.alt} className="mt-10 aspect-[16/9] w-full rounded-[16px] object-cover lg:aspect-[21/9]" data-id="element-1795" data-mp="159kq7dn34sez" />
        </div>
      </header>

      <div className="mx-auto grid max-w-shell grid-cols-1 gap-12 px-6 py-12 lg:grid-cols-[minmax(0,720px)_280px] lg:justify-between lg:py-16" data-id="element-1796" data-mp="1a6t8rfc5aygr">
        <div ref={articleRef} className="min-w-0" data-id="element-1797" data-mp="1o5atr5ao1wuz">
          {isDraft && <PlaceholderNote className="mb-8" data-id="element-1798" data-mp="1j79b2lh5jzk1">
              Sample article: the full text for this post is in preparation. Only the summary is shown.
            </PlaceholderNote>}
          {article.intro && <div className="space-y-6" data-id="element-1799" data-mp="3l05titde64e6">
              {article.intro.map((block, i) => <ArticleBlockView key={i} block={block} data-id="element-1800" data-mp="20hv3qhxe5g3i" data-mpi={i} />)}
            </div>}
          {sections.map(section => <section key={section.id} id={section.id} aria-labelledby={`${section.id}-h`} className="scroll-mt-[104px] pt-12" data-id="element-1801" data-mp="25hr66cqfwzgy">
              <h2 id={`${section.id}-h`} className="group display flex items-center gap-2 text-[28px] font-semibold text-ink sm:text-[32px]" data-id="element-1802" data-mp="1vplel443ok2a">
                {section.title}
                <a href={`#${section.id}`} aria-label={`Link to ${section.title}`} className="text-[20px] text-ink-muted opacity-0 transition-opacity duration-150 ease-out focus:opacity-100 group-hover:opacity-100" data-id="element-1803" data-mp="1hsopb73mrani">
                  #
                </a>
              </h2>
              <div className="mt-5 space-y-6" data-id="element-1804" data-mp="334q5kv8iolij">
                {section.blocks.map((block, i) => <ArticleBlockView key={i} block={block} data-id="element-1805" data-mp="3748r9t85x0mf" data-mpi={i} />)}
              </div>
            </section>)}

          <div className="mt-14 space-y-8" data-id="element-1806" data-mp="jy45g591vrs6">
            <ArticleFeedback data-id="element-1807" data-mp="ex849zg32jqg" />
            <AuthorBio author={author} data-id="element-1808" data-mp="ewygmiiigonx" />
            <ArticleTags tags={article.tags} data-id="element-1809" data-mp="f02n5mjg7whr" />
          </div>
        </div>

        <aside className="hidden lg:block" aria-label="Article sidebar" data-id="element-1810" data-mp="awegr76jz6y3">
          <div className="sticky top-[104px] space-y-8" data-id="element-1811" data-mp="5yczmrsothp9">
            <ArticleToc items={tocItems} activeId={activeId} data-id="element-1812" data-mp="12naro291z1iq" />
            <SidebarProductCard productKey={article.product} data-id="element-1813" data-mp="12miy0wz0ndv3" />
          </div>
        </aside>
      </div>

      <section className="w-full bg-canvas-warm py-20" aria-labelledby="related-articles-heading" data-id="element-1814" data-mp="ymihn6scwsle">
        <div className="mx-auto max-w-shell px-6" data-id="element-1815" data-mp="tsw5rgu0osqn">
          <h2 id="related-articles-heading" className="display text-[30px] font-semibold text-ink sm:text-[40px]" data-id="element-1816" data-mp="owpachz8yzmy">
            Keep reading
          </h2>
          <ul className="mt-10 grid grid-cols-1 gap-x-6 gap-y-12 md:grid-cols-3" data-id="element-1817" data-mp="1mde55rps51mu">
            {related.map(a => <li key={a.slug} data-id="element-1818" data-mp="1ovpsuye2ho5n">
                <ArticleCard article={a} data-id="element-1819" data-mp="1owretxwmfv25" />
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand headline="Run your business with clarity and control" subheadline="Sales, stock, expenses, and reports in one place. Set up in minutes." primary={{
      label: 'Get started',
      href: '/get-started'
    }} secondary={{
      label: 'Book a demo',
      href: '/demo'
    }} data-id="element-1820" data-mp="1tvce9v41uapj" />

      <div className={`fixed inset-x-0 bottom-0 z-30 border-t border-canvas-line bg-white/95 px-3 py-2 backdrop-blur transition-[transform,opacity] duration-200 ease-out lg:hidden ${barVisible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-full opacity-0'}`} style={{
      paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))'
    }} data-id="element-1821" data-mp="27u2az9myi8dd">
        <ShareButtons title={article.title} variant="bar" data-id="element-1822" data-mp="2qmefa1x67jq5" />
      </div>
    </main>;
}
function draftSections(article: Article): ArticleSection[] {
  const product = productCatalog[article.product];
  return [{
    id: 'summary',
    title: 'In summary',
    blocks: [{
      type: 'p',
      text: article.excerpt
    }, {
      type: 'p',
      text: article.subtitle
    }]
  }, {
    id: 'next-steps',
    title: 'Next steps',
    blocks: [{
      type: 'cta',
      title: product ? `See how ${product.title} helps` : 'See how Kayko helps',
      text: product?.copy ?? 'Sales, stock, expenses, and reports in one place.',
      label: 'Learn more',
      href: product?.href ?? '/get-started'
    }]
  }];
}